import { autorun, runInAction, makeAutoObservable } from 'mobx';
import { DefaultFormFields, FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { RootStore } from 'store/rootStore';
import { Group, InputGroup } from 'types/groupService';
import { convertAoRToInput } from 'store/domain/agentsStore';
import { Primitive } from 'types/general';

interface GroupFormFields extends DefaultFormFields {
  name: FormControl<string>;
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  primaryPhone: FormControl<string>;
  primaryEmail: FormControl<string>;
  title: FormControl<string>;
  city: FormControl<string>;
  street: FormControl<string>;
  street2: FormControl<string>;
  zip: FormControl<string>;
  state: FormControl<string>;
  county: FormControl<string>;
}

export class GroupInfoModalStore {
  rootStore: RootStore;
  groupForm: FormController<GroupFormFields>;

  loading = false;

  public async requestZipCodeInfo(): Promise<void> {
    this.setLoading(true);
    await this.rootStore.geoStore.requestZipCodeInfo(this.groupForm.fields.zip.value);

    runInAction(() => {
      this.setLoading(false);
      const { wrongZipCodeKeyedIn, zipCodeAffiliatedData } = this.rootStore.geoStore;
      if (!wrongZipCodeKeyedIn) {
        this.groupForm.fields.state.value = zipCodeAffiliatedData.state.id;
        this.groupForm.fields.county.value = zipCodeAffiliatedData.counties[0].name;
      }
    });
  }

  public async saveGroupOnServer(): Promise<void> {
    this.setLoading(true);
    const groupFromFormAnswers: InputGroup = {
      source: 'AGENT_V2',
      name: this.groupForm.fields.name.value,
      contactInfo: {
        firstName: this.groupForm.fields.firstName.value,
        lastName: this.groupForm.fields.lastName.value,
        primaryPhone: this.groupForm.fields.primaryPhone.value,
        primaryEmail: this.groupForm.fields.primaryEmail.value,
        title: this.groupForm.fields.title.value,
        primaryAddress: {
          city: this.groupForm.fields.city.value,
          street: this.groupForm.fields.street.value,
          street2: this.groupForm.fields.street2.value,
          zip: this.groupForm.fields.zip.value,
          state: this.groupForm.fields.state.value,
          county: this.groupForm.fields.county.value,
        },
      },
      agentOfRecord: convertAoRToInput(this.rootStore.groupStore.group.agentOfRecord),
      accountRepresentative: this.rootStore.groupStore.group.accountRepresentative,

      effectiveDate: '',
      id: this.rootStore.groupStore.group.id,
    };
    await this.rootStore.groupStore.updateGroup(groupFromFormAnswers);
    runInAction(() => {
      this.setLoading(false);
    });
  }

  public setLoading(status: boolean): void {
    this.loading = status;
  }

  public updateFormWithData(data: Pick<Group, 'name' | 'contactInfo'>): void {
    this.groupForm.populateFields(data as unknown as Record<string, Primitive>);
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    this.groupForm = new FormController({
      name: new FormControl<string>('', { validations: ['printableCharacters'] }),
      firstName: new FormControl<string>('', { optional: true }),
      lastName: new FormControl<string>('', { optional: true }),
      primaryPhone: new FormControl<string>('', { validations: ['isValidUSAPhoneNumber'], optional: true }),
      primaryEmail: new FormControl<string>('', { validations: ['isEmailValid'], optional: true }),
      title: new FormControl<string>('', { optional: true }),
      city: new FormControl<string>('', { validations: ['printableCharacters'], optional: true }),
      street: new FormControl<string>('', {
        optional: true,
        requiredWithFields: ['street2'],
      }),
      street2: new FormControl<string>('', { optional: true }),
      zip: new FormControl<string>('', {
        validations: ['containsOnlyNumbers'],
        optional: true,
        requiredWithFields: ['state', 'county'],
      }),
      state: new FormControl<string>('', {
        optional: true,
        requiredWithFields: ['zip', 'county'],
      }),
      county: new FormControl<string>('', {
        optional: true,
        requiredWithFields: ['zip', 'state'],
      }),
    });

    autorun(() => {
      if (this.groupForm.fields.zip.value.length === 5) {
        void this.requestZipCodeInfo();
      } else {
        this.groupForm.fields.state.overrideValue('');
        this.groupForm.fields.county.overrideValue('');
      }
    });

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
